import { ReactElement } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { ApiParameter } from "../api/types.gen.ts";
import { HasHttpParameters } from "../types/form-inputs.ts";
import APIParameterRow from "./APIParameterRow.tsx";
import { CardLayout } from "./CardLayout.tsx";
import { Button } from "./catalyst/Button.tsx";
import { Table } from "./catalyst/Table.tsx";
import { TableBody } from "./catalyst/TableBody.tsx";
import { TableCell } from "./catalyst/TableCell.tsx";
import { TableHead } from "./catalyst/TableHead.tsx";
import { TableHeader } from "./catalyst/TableHeader.tsx";
import { TableRow } from "./catalyst/TableRow.tsx";

export type APIParameterInlineTableProps = {
  currentParameterType: ApiParameter["type"];
  showDescription?: boolean;
};

const APIParameterInlineTable = ({
  currentParameterType,
  showDescription = false,
}: APIParameterInlineTableProps): ReactElement => {
  const { control } = useFormContext<HasHttpParameters>();
  const { append, fields, remove } = useFieldArray({
    control,
    name: "httpParameters",
  });

  const addNewRow = (): void => {
    append({
      description: "",
      name: "",
      type: currentParameterType, // Ensure this matches the expected "Header" | "QueryParameter"
      value: "",
    });
  };

  const parameterList = fields.map((rawParameter, i) => {
    const parameter = rawParameter;
    if (String(parameter.type) !== currentParameterType) return null;

    return (
      <APIParameterRow
        index={i}
        key={rawParameter.id}
        parameter={rawParameter}
        remove={remove}
        showDescription={showDescription}
      />
    );
  });

  const addButton = (
    <Button onClick={() => addNewRow()} outline>
      + Add
    </Button>
  );
  let tableBody = <>{parameterList}</>;

  if (parameterList.filter((p) => p !== null).length === 0) {
    tableBody = (
      <TableRow>
        <TableCell
          className="border border-gray-300 py-4 text-center text-gray-500"
          colSpan={4}
        >
          <span className="mr-4 text-gray-500">
            No {currentParameterType} parameters
          </span>
          {addButton}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <CardLayout
        header={
          <>
            <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div className="ml-4 mt-2">{currentParameterType}</div>
              <div className="ml-4 mt-2 shrink-0">{addButton}</div>
            </div>
          </>
        }
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-1/4 text-left text-gray-300">
                Name
              </TableHeader>
              <TableHeader className="w-2/4 text-left text-gray-300">
                Value
              </TableHeader>
              {showDescription && (
                <TableHeader className="text-left text-gray-300">
                  Description
                </TableHeader>
              )}
              <TableHeader className="w-10 text-right" />
            </TableRow>
          </TableHead>
          <TableBody>{tableBody}</TableBody>
        </Table>
      </CardLayout>
    </>
  );
};

export default APIParameterInlineTable;
